import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';
import { FieldValidationType } from '../../../../sic-lib/enums/FieldValidationType';
import { Contact } from '../../../../sic-lib/types/Contact';
import { ContactAddress, ContactField } from '../../../../sic-lib/types/ContactField';
import { ListType } from '../../../../sic-lib/types/Lists';
import { ViewProperty } from '../../../../sic-lib/types/ViewProperty';
import { stringifyContactAddress } from '../../../../sic-lib/utils/contactFieldHelpers';
import { hasMatchingValidationType } from '../../../../sic-lib/utils/functionalFilters';
import { mapContactFieldsToView } from '../../../../sic-lib/utils/functionalMappers';
import DisplayDateTime from '../../../Shared/DisplayDateTime/DisplayDateTime';
import DisplayPhoneNumber from '../../../Shared/DisplayPhoneNumber/DisplayPhoneNumber';
import TableContactInfo from '../TableContactInfo/TableContactInfo';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        /* 
            IMPORTANT: this positioning makes the background change on contact name hover possible;
            no child element should have positioning that might interfere with this effect.
         */
        position: 'relative',
        height: '4.5rem',
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        /*
            until a better solution is thought of: this is needed to place cell text, inputs above
            background change on contact name hover
         */
        '& .MuiCheckbox-root, & .MuiTypography-root': {
            position: 'relative',
            zIndex: 1,
        },
    },
    gridItemPrimary: {
        [theme.breakpoints.up(0)]: {
            flexBasis: 0,
            flexGrow: 1,
            flexShrink: 0,
        },
        [theme.breakpoints.up('md')]: {
            flexBasis: '320px',
            flexGrow: 0,
            maxWidth: '320px',
        },
    },
    gridItemSecondary: {
        [theme.breakpoints.up('md')]: {
            flexBasis: '240px',
            flexGrow: 1,
            flexShrink: 0,
            width: '0px',
            padding: theme.spacing(1),
            overflow: 'hidden',
        },
    },
}));

interface Props {
    contact: Contact;
    columnTemplate: ViewProperty[];
    appliedListId: number;
    appliedListType: ListType;
    hasMediumScreen: boolean;
}

export default function ContactsTableRow({
    contact,
    columnTemplate,
    appliedListId,
    appliedListType,
    hasMediumScreen,
}: Props) {
    const { classes } = useStyles();

    const [contactCells, setContactCells] = React.useState<ContactField[]>([]);

    React.useEffect(() => {
        const cells = columnTemplate.map(mapContactFieldsToView(contact.values));

        setContactCells(cells);
    }, [columnTemplate, contact.values]);

    return (
        <Grid container direction="row" wrap="nowrap" alignItems="center" className={classes.root}>
            <Grid item className={classes.gridItemPrimary}>
                <TableContactInfo
                    contact={contact}
                    appliedListId={appliedListId}
                    appliedListType={appliedListType}
                />
            </Grid>
            {hasMediumScreen &&
                contactCells.map(val => (
                    <Grid key={uuidv4()} item className={classes.gridItemSecondary}>
                        {hasMatchingValidationType(FieldValidationType.date)(val) && (
                            <DisplayDateTime dateText={val.value as string} />
                        )}
                        {hasMatchingValidationType(FieldValidationType.usMobilePhone)(val) && (
                            <Typography component="span" variant="body2">
                                <DisplayPhoneNumber phoneNumber={val.value as string} />
                            </Typography>
                        )}
                        {hasMatchingValidationType(FieldValidationType.address)(val) && (
                            <Typography component="span" variant="body2">
                                {stringifyContactAddress(val.value as ContactAddress)}
                            </Typography>
                        )}
                        {!hasMatchingValidationType([
                            FieldValidationType.usMobilePhone,
                            FieldValidationType.date,
                            FieldValidationType.address,
                        ])(val) && (
                            <Typography component="span" variant="body2">
                                {val.value}
                            </Typography>
                        )}
                    </Grid>
                ))}
        </Grid>
    );
}
